import Flex from '@components/Flex/Flex';
import Slider from '@components/Slider/Slider';
import Text from '@components/Text/Text';
import { useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { StyledContainer, StyledInput } from './SliderContainer.styles';
import { SliderContainerProps } from './SliderContainer.types';

const SliderContainer: React.FC<SliderContainerProps> = ({
  children,
  label,
  endValueAdornment,
  startValueAdornment,
  value,
  hasInput = false,
  onChange,
  ...restProps
}) => {
  const theme = useTheme();
  const [inputWidth, setInputWidth] = useState<number>(15);
  const [inputValue, setInputValue] =
    useState<number | number[] | undefined>(value);

  useEffect(() => {
    if (inputValue && inputValue?.toString()?.length > 1) {
      setInputWidth(inputValue?.toString().length * 12);
    } else {
      setInputWidth(15);
    }
  }, [inputValue]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const textInputValueChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    if (!onChange) return;
    const newValue = Number(e.target.value.replace(/[^0-9]/g, ''));
    const min = Number(restProps.min);
    const max = Number(restProps.max);
    setInputValue(newValue);
    if (newValue >= min && max && newValue <= max) {
      onChange(undefined, newValue, newValue);
    }
  };

  const isError = useMemo(() => {
    const min = Number(restProps.min);
    const max = Number(restProps.max);
    if (!inputValue && min > 0) {
      return true;
    }
    if (min && inputValue && inputValue < min) {
      return true;
    } else if (max && inputValue && inputValue > max) {
      return true;
    }
    return false;
  }, [inputValue, restProps.max, restProps.min]);

  return (
    <StyledContainer>
      <Flex justifyContent='space-between' alignItems='center' gap={3}>
        <Text fontSize={14} variant='h6' fontWeight='fontWeightSemiBold'>
          {label}
        </Text>
        {hasInput ? (
          <StyledInput
            value={inputValue?.toLocaleString()}
            fullWidth={false}
            inputProps={{
              style: {
                textAlign: 'end',
                padding: 0,
                width: inputWidth,
              },
            }}
            disableUnderline
            inputMode='numeric'
            startAdornment={startValueAdornment}
            endAdornment={endValueAdornment}
            onChange={textInputValueChange}
            error={isError}
          />
        ) : (
          <Text fontSize={14} variant='h6' fontWeight='fontWeightSemiBold'>
            {startValueAdornment}
            {value?.toLocaleString()}
            {endValueAdornment}
          </Text>
        )}
      </Flex>
      <Slider
        sx={{ height: 5, padding: 0 }}
        orientation='horizontal'
        {...restProps}
        value={value}
        onChange={onChange}
      />
      <Flex justifyContent='space-between' alignItems='center' gap={3} mt={5}>
        <Text
          variant='body'
          fontWeight='fontWeightMedium'
          color={theme.palette.grey.A100}
        >
          {startValueAdornment}
          {restProps?.min?.toLocaleString()}
          {endValueAdornment}
        </Text>
        <Text
          variant='body'
          fontWeight='fontWeightMedium'
          color={theme.palette.grey.A100}
        >
          {startValueAdornment}
          {restProps?.max?.toLocaleString()}
          {endValueAdornment}
        </Text>
      </Flex>
      {children && <Flex mt={8}>{children}</Flex>}
    </StyledContainer>
  );
};

export default SliderContainer;
